import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useLocation, useOutletContext, useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Pagination } from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import PageHeader from "../PageHeader";
import usePageParam from "../../hooks/usePageParam";
import { investingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import ResearchThisDialog from "./ResearchThisDialog";
import useTheses from "./useTheses";
import useUnlinked from "./useUnlinked";
import VerdictBadge from "./VerdictBadge";
import {
  VERDICTS,
  companyPath,
  formatDay,
  formatMarketCap,
  formatScore,
  scoreTone,
  taskBoardPath,
  topScores,
  verdictLabel,
} from "./lib/format";
import {
  SECTORS,
  hasFilters,
  orderingOptions,
  readQuery,
  withFilter,
  withoutFilters,
} from "./lib/researchQuery";

// The API's page size for companies (constants.py COMPANY_PAGE_SIZE) — not
// the house's 12: the universe is ~1,000 rows.
const PAGE_SIZE = 100;
const CONTROL = "h-11 md:h-9";

/** "Top 3 of 7 scores: a +2, b +1, c 0" — how many, and which. */
function ScoreSummary({ scores }) {
  const { shown, total } = topScores(scores);
  if (total === 0) return <span className="text-muted-foreground">No scores yet</span>;
  const lead =
    shown.length < total
      ? `Top ${shown.length} of ${total} scores:`
      : `${total} ${total === 1 ? "score" : "scores"}:`;
  return (
    <span>
      <span className="whitespace-nowrap text-muted-foreground">{lead}</span>
      {/* A phone wraps between scores, and inside a long slug (up to 60
          characters) rather than running off the card; the no-break space
          keeps each score beside the end of its slug. */}
      {shown.map((item, i) => (
        <Fragment key={item.thesis}>
          {i === 0 ? " " : ", "}
          <span className="wrap-anywhere">
            {item.thesis}
            {"\u00A0"}
            <span className={cn("font-semibold tabular-nums", scoreTone(item.score))}>
              {formatScore(item.score)}
            </span>
          </span>
        </Fragment>
      ))}
    </span>
  );
}

ScoreSummary.propTypes = {
  scores: PropTypes.arrayOf(
    PropTypes.shape({ thesis: PropTypes.string.isRequired, score: PropTypes.number.isRequired }),
  ),
};

/** The id of a row's research control (the button or the task link). */
const actionId = (symbol) => `research-action-${symbol}`;

// A responsive table: a real table from md, a card per company below it.
// The explicit roles keep it a table to assistive tech when CSS changes a
// row's display (browsers drop table semantics on display:grid/block).
const CELL = "block md:table-cell md:px-3 md:py-2.5 md:align-top";

function CompanyRow({ row, search, canRequest, unlinked, requested, onRequest }) {
  const cap = formatMarketCap(row.market_cap);
  const researched = formatDay(row.last_researched_at);
  let research = null;
  if (row.research_requested && row.requested_task) {
    research = (
      <>
        <Link
          id={actionId(row.symbol)}
          to={taskBoardPath(row.requested_task)}
          className="inline-flex min-h-11 items-center underline underline-offset-2 md:min-h-0"
        >
          Research requested — task #{row.requested_task}
        </Link>
        {requested && requested.taskId === row.requested_task && (
          <span className="block text-xs text-muted-foreground">
            {requested.agent
              ? `Assigned to ${requested.agent} — its note lands on ${row.symbol}'s page.`
              : `Its note lands on ${row.symbol}'s page.`}
          </span>
        )}
      </>
    );
  } else if (unlinked) {
    research = (
      <span className="flex flex-col items-start gap-1">
        <span className="text-sm">Task #{unlinked.id} isn&apos;t linked yet.</span>
        {canRequest && (
          <Button
            id={actionId(row.symbol)}
            type="button"
            variant="outline"
            size="sm"
            className="h-11 md:h-8"
            onClick={() => onRequest(row)}
          >
            Link task #{unlinked.id}
            <span className="sr-only">: {row.symbol}</span>
          </Button>
        )}
      </span>
    );
  } else if (canRequest) {
    research = (
      <Button
        id={actionId(row.symbol)}
        type="button"
        variant="outline"
        size="sm"
        className="h-11 md:h-8"
        onClick={() => onRequest(row)}
      >
        Research this
        <span className="sr-only">: {row.symbol}</span>
      </Button>
    );
  }
  return (
    <tr
      role="row"
      className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 rounded-lg border border-border bg-card p-3 md:table-row md:rounded-none md:border-0 md:border-b md:bg-transparent md:p-0"
    >
      <td role="cell" className={cn(CELL, "min-w-0")}>
        <Link
          to={companyPath(row.symbol)}
          state={{ researchSearch: search }}
          className="inline-flex min-h-11 min-w-11 items-center font-semibold underline-offset-2 hover:underline md:min-h-0 md:min-w-0"
        >
          {row.symbol}
        </Link>{" "}
        <span className="text-sm text-muted-foreground wrap-anywhere">{row.name}</span>
        {!row.in_universe && (
          <>
            {" "}
            <Badge variant="outline" title="Taken out of the universe by hand">
              De-listed
            </Badge>
          </>
        )}
      </td>
      <td role="cell" className={cn(CELL, "justify-self-end text-right md:text-left")}>
        <VerdictBadge verdict={row.verdict} conviction={row.conviction} />
        {row.proposed_verdict && (
          <>
            {" "}
            <Badge
              variant="outline"
              className="border-warning"
              title="Research proposed a verdict — awaiting your decision"
            >
              Proposed: {verdictLabel(row.proposed_verdict)}
            </Badge>
          </>
        )}
      </td>
      <td role="cell" className={cn(CELL, "text-sm")}>
        {row.sector || <span className="text-muted-foreground">Sector unknown</span>}
      </td>
      <td role="cell" className={cn(CELL, "justify-self-end text-right text-sm tabular-nums")}>
        {cap ?? <span className="text-muted-foreground">Market cap unknown</span>}
      </td>
      <td role="cell" className={cn(CELL, "col-span-2 text-sm")}>
        <ScoreSummary scores={row.scores} />
      </td>
      <td role="cell" className={cn(CELL, "col-span-2 text-sm md:whitespace-nowrap")}>
        {researched ? (
          `Researched ${researched}`
        ) : (
          <span className="text-muted-foreground">Not researched yet</span>
        )}
      </td>
      <td role="cell" className={cn(CELL, "col-span-2 text-sm")}>
        {research}
      </td>
    </tr>
  );
}

CompanyRow.propTypes = {
  row: PropTypes.shape({
    symbol: PropTypes.string.isRequired,
    name: PropTypes.string,
    sector: PropTypes.string,
    market_cap: PropTypes.string,
    in_universe: PropTypes.bool,
    verdict: PropTypes.string.isRequired,
    conviction: PropTypes.number,
    last_researched_at: PropTypes.string,
    scores: PropTypes.array,
    proposed_verdict: PropTypes.string,
    research_requested: PropTypes.bool,
    requested_task: PropTypes.number,
  }).isRequired,
  /** The list's query (`?…`), carried to the company's page for its way back. */
  search: PropTypes.string,
  canRequest: PropTypes.bool.isRequired,
  unlinked: PropTypes.shape({ id: PropTypes.number.isRequired }),
  requested: PropTypes.shape({ taskId: PropTypes.number, agent: PropTypes.string }),
  onRequest: PropTypes.func.isRequired,
};

function FilterSelect({ id, label, value, onChange, children, help }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <NativeSelect
        id={id}
        value={value}
        className={CONTROL}
        aria-describedby={help ? `${id}-help` : undefined}
        onChange={(event) => onChange(event.target.value)}
      >
        {children}
      </NativeSelect>
      {help && (
        <p id={`${id}-help`} className="sr-only">
          {help}
        </p>
      )}
    </div>
  );
}

FilterSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  help: PropTypes.string,
};

/**
 * Research (spec §5.1, walkthroughs 1 and 2): every company we've looked
 * at, 100 a page (the API's page size), with its verdict, sector, market
 * cap, top thesis scores, when it was last researched, any research
 * requested, and any verdict research proposed that awaits the person.
 *
 * The query lives in the URL — verdict, thesis, sector, researched,
 * proposed, the search and the sort, and the page — so a refresh, a link or
 * Back lands on the same list. A filter change drops the page in the SAME
 * URL write (never the old page under the new filter), and replaces the
 * history entry; paging pushes one. A page past the end heals to page 1.
 * Only the newest request lands.
 *
 * Screen figures aren't in the rows (PR 3's call): sector and market cap
 * are; the figures are on the company's page, "as of" their date.
 *
 * "Research this" is a person's: it needs their agents (a person's read)
 * and makes a board task in their name. It shows only once `me` says
 * human — never while unknown, never to an agent. A task whose link failed
 * is kept in the section's store (`unlinked`, InvestingLayout): the row —
 * and the company's page — offer "Link task #N", never a second task, for
 * as long as this tab remembers it.
 */
function InvestingResearch() {
  const outlet = useOutletContext() || {};
  const { me } = outlet;
  // The section's store; on its own (outside the layout), this page's.
  const ownUnlinked = useUnlinked(null);
  const unlinked = outlet.unlinked ?? ownUnlinked;
  const { setTask } = unlinked;
  const canRequest = me?.kind === "human";
  const formId = useId();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = usePageParam();
  const query = readQuery(searchParams);
  const { verdict, thesis, sector, researched, proposed, q, ordering } = query;
  const filtered = hasFilters(query);
  const { theses, error: thesesError } = useTheses();
  const titles = useMemo(
    () => Object.fromEntries(theses.map((t) => [t.slug, t.title])),
    [theses],
  );

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [qInput, setQInput] = useState(q);
  // Research this: the dialog's company, and the requests made from this
  // page (to say who has them). Tasks made but not linked are `unlinked`.
  const [dialogCompany, setDialogCompany] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [requested, setRequested] = useState({});

  // The search box follows the URL (Back, Clear filters).
  useEffect(() => {
    setQInput(q);
  }, [q]);

  // Only the newest listing lands; `inFlight` says one is out.
  const requestRef = useRef(0);
  const inFlight = useRef(false);
  useEffect(() => {
    const request = ++requestRef.current;
    const isLatest = () => request === requestRef.current;
    inFlight.current = true;
    setLoading(true);
    setError(null);
    const filters = Object.fromEntries(
      Object.entries({ verdict, thesis, sector, researched, proposed, q, ordering }).filter(
        ([, value]) => value,
      ),
    );
    investingApi.companies.list({ ...filters, page }).then(
      ({ data }) => {
        if (!isLatest()) return;
        inFlight.current = false;
        setRows(data?.results || []);
        setCount(data?.count || 0);
        setLoading(false);
      },
      (err) => {
        if (!isLatest()) return;
        inFlight.current = false;
        // A page past the end (the list shrank, an old link): page 1.
        if (err?.response?.status === 404 && page > 1) {
          setPage(1, { replace: true });
          return;
        }
        setError(err);
        setLoading(false);
      },
    );
  }, [verdict, thesis, sector, researched, proposed, q, ordering, page, setPage, reloadTick]);

  const setFilter = (name, value) => {
    setSearchParams(withFilter(searchParams, name, value), { replace: true });
  };
  const clearFilters = () => {
    setQInput("");
    setSearchParams(withoutFilters(searchParams), { replace: true });
  };
  const search = (event) => {
    event.preventDefault();
    setFilter("q", qInput.trim());
  };

  const openRequest = (row) => {
    setDialogCompany({ symbol: row.symbol, name: row.name });
    setDialogOpen(true);
  };
  const taskCreated = useCallback((symbol, task) => setTask(symbol, task), [setTask]);
  const forgetTask = useCallback((symbol) => setTask(symbol, null), [setTask]);
  const linked = useCallback((symbol, company, task) => {
    forgetTask(symbol);
    setRequested((prev) => ({
      ...prev,
      [symbol]: { taskId: task.id, agent: task.assignee?.username ?? null },
    }));
    setRows((prev) =>
      prev.map((row) =>
        row.symbol === symbol
          ? {
              ...row,
              research_requested: company.research_requested,
              requested_task: company.requested_task,
            }
          : row,
      ),
    );
    // A listing sent before the link would land without it: ask again.
    if (inFlight.current) setReloadTick((tick) => tick + 1);
  }, [forgetTask]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const first = (page - 1) * PAGE_SIZE + 1;
  const last = Math.min(page * PAGE_SIZE, count);
  const sortValue = ordering === "symbol" ? "" : ordering;
  const thesisOptions = [...theses];
  if (thesis && !theses.some((t) => t.slug === thesis)) {
    thesisOptions.push({ slug: thesis, title: thesis, status: "active" });
  }
  const sectorOptions = sector && !SECTORS.some((s) => s.toLowerCase() === sector.toLowerCase())
    ? [...SECTORS, sector]
    : SECTORS;
  const sectorValue = SECTORS.find((s) => s.toLowerCase() === sector.toLowerCase()) ?? sector;

  return (
    <div className="page-container mt-6 pt-4">
      <PageHeader
        title="Research"
        subtitle="Every company we've looked at — its verdict, its scores against our theses (−2 to +2), and when it was last researched. Research on any of them is one tap away: Research this puts a task on the board for one of your agents."
      />

      <form onSubmit={search} className="mb-3 flex max-w-xl" role="search">
        <Label htmlFor={`${formId}-q`} className="sr-only">
          Search symbol or name
        </Label>
        <Input
          id={`${formId}-q`}
          type="search"
          value={qInput}
          maxLength={100}
          onChange={(event) => setQInput(event.target.value)}
          placeholder="Symbol or name, e.g. NVDA or Nvidia"
          className={cn(CONTROL, "rounded-r-none")}
        />
        <Button type="submit" variant="outline" className={cn(CONTROL, "rounded-l-none border-l-0")}>
          Search
        </Button>
      </form>

      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <FilterSelect id={`${formId}-verdict`} label="Verdict" value={verdict} onChange={(v) => setFilter("verdict", v)}>
          <option value="">Any verdict</option>
          {VERDICTS.map((v) => (
            <option key={v.value} value={v.value}>
              {v.label}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect
          id={`${formId}-thesis`}
          label="Thesis"
          value={thesis}
          help="Companies with a current score on this thesis."
          onChange={(v) => setFilter("thesis", v)}
        >
          <option value="">Any thesis</option>
          {thesisOptions.map((t) => (
            <option key={t.slug} value={t.slug}>
              {t.title}
              {t.status === "retired" ? " (retired)" : ""}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect id={`${formId}-sector`} label="Sector" value={sectorValue} onChange={(v) => setFilter("sector", v)}>
          <option value="">Any sector</option>
          {sectorOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect id={`${formId}-researched`} label="Research" value={researched} onChange={(v) => setFilter("researched", v)}>
          <option value="">Any</option>
          <option value="yes">Researched</option>
          <option value="no">Not researched yet</option>
        </FilterSelect>
        <FilterSelect
          id={`${formId}-proposed`}
          label="Proposed verdict"
          value={proposed}
          help="Research proposed a verdict you haven't decided yet."
          onChange={(v) => setFilter("proposed", v)}
        >
          <option value="">Any</option>
          <option value="yes">Awaiting your decision</option>
          <option value="no">None awaiting you</option>
        </FilterSelect>
        <FilterSelect id={`${formId}-sort`} label="Sort" value={sortValue} onChange={(v) => setFilter("ordering", v)}>
          {orderingOptions({ thesis, ordering: sortValue }, titles).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </FilterSelect>
      </div>
      {thesesError && (
        <p className="mb-3 text-sm text-muted-foreground">
          Couldn&apos;t load the theses for the filter ({apiErrorMessage(thesesError, "error")}) — the
          rest of the list works.
        </p>
      )}

      <div className="mb-3 flex min-h-11 flex-wrap items-center gap-3">
        <p role="status" className="m-0 text-sm text-muted-foreground">
          {loading
            ? "Loading companies…"
            : error
              ? ""
              : count === 0
                ? ""
                : `${count.toLocaleString("en-US")} ${count === 1 ? "company" : "companies"}${
                    filtered ? (count === 1 ? " matches" : " match") : ""
                  } — page ${page} of ${totalPages}, showing ${first}–${last}.`}
        </p>
        {filtered && (
          <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
      </div>

      {error ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription className="flex flex-wrap items-center gap-2">
            <span>{apiErrorMessage(error, "Couldn't load the companies.")}</span>{" "}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-11 md:h-8"
              onClick={() => setReloadTick((tick) => tick + 1)}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      ) : loading && rows.length === 0 ? (
        <div className="py-12 text-center">
          <Loader2 className="inline size-6 animate-spin text-muted-foreground" aria-hidden="true" />
        </div>
      ) : !loading && count === 0 ? (
        filtered ? (
          <div className="rounded-lg border bg-card p-4">
            <p className="m-0 mb-3">No company matches these filters.</p>
            <Button type="button" variant="outline" className={CONTROL} onClick={clearFilters}>
              Clear filters
            </Button>
          </div>
        ) : (
          <div className="rounded-lg border bg-card p-4">
            <p className="m-0">
              No companies yet. The universe arrives when an agent works the board task
              &ldquo;Investing: screen the Russell 1000&rdquo; — about 1,000 rows, each with its
              sector, market cap and screen figures. Research on a company adds it too.
            </p>
          </div>
        )
      ) : (
        <table
          role="table"
          aria-label="Companies"
          aria-busy={loading || undefined}
          className={cn("block w-full border-collapse text-left md:table", loading && "opacity-60")}
        >
          <thead role="rowgroup" className="sr-only md:not-sr-only md:table-header-group">
            <tr role="row" className="border-b border-border text-xs text-muted-foreground">
              {["Company", "Verdict", "Sector", "Market cap", "Top scores", "Last researched", "Research"].map(
                (name) => (
                  <th
                    key={name}
                    role="columnheader"
                    scope="col"
                    className={cn("px-3 py-2 font-medium", name === "Market cap" && "text-right")}
                  >
                    {name}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody role="rowgroup" className="flex flex-col gap-3 md:table-row-group">
            {rows.map((row) => (
              <CompanyRow
                key={row.symbol}
                row={row}
                search={location.search}
                canRequest={canRequest}
                unlinked={unlinked.tasks[row.symbol] ?? null}
                requested={requested[row.symbol] ?? null}
                onRequest={openRequest}
              />
            ))}
          </tbody>
        </table>
      )}

      {!error && totalPages > 1 && (
        <Pagination className="my-6" page={page} total={totalPages} onChange={(next) => setPage(next)} showEdges />
      )}

      {canRequest && (
        <ResearchThisDialog
          open={dialogOpen}
          company={dialogCompany}
          me={me}
          pendingTask={dialogCompany ? unlinked.tasks[dialogCompany.symbol] ?? null : null}
          onOpenChange={setDialogOpen}
          onTaskCreated={taskCreated}
          onLinked={linked}
          onForgetTask={forgetTask}
          onCloseAutoFocus={(event) => {
            // The row's control changed under the dialog (the button became
            // the task link, or "Link task #N"): focus whichever it is now.
            const target = dialogCompany && document.getElementById(actionId(dialogCompany.symbol));
            if (target) {
              event.preventDefault();
              target.focus();
            }
          }}
        />
      )}
    </div>
  );
}

export default InvestingResearch;
